import { useState, useCallback } from "react"
import { paymentService } from "../services/payment-api"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { usePurchaseStore } from "@/stores/purchase-store"
import { useQuizStore } from "@/stores/quiz-store"
import { showErrorToast } from "@/stores/toast-store"
import type { ProductPlan } from "@/features/products/types"
import { paymentErrorMessage } from "../domain/payment-error"
import type { PaymentStatus } from "../types"

export interface UseUpsellResult {
  status: PaymentStatus
  error: string | null
  /** Whether an upsell can be charged (we have the original subscription id). */
  canPurchase: boolean
  /** Charges the plan; resolves to the new subscription id, or null on failure. */
  purchase: (
    plan: ProductPlan,
    extra?: { additionalOffer: true },
  ) => Promise<string | null>
}

/**
 * Charges an additional plan to the card saved during the initial purchase,
 * using the original subscription id (no new card entry required).
 */
export function useUpsell(): UseUpsellResult {
  const subscriptionId = usePurchaseStore((s) => s.paymentIntentId)
  const email = (useQuizStore((s) => s.answers.email) as string) || ""
  const [status, setStatus] = useState<PaymentStatus>("idle")
  const [error, setError] = useState<string | null>(null)

  const purchase = useCallback(
    async (plan: ProductPlan, extra?: { additionalOffer: true }): Promise<string | null> => {
      if (status === "processing") return null
      if (!subscriptionId) {
        setError("No active order to attach this offer to.")
        return null
      }

      setStatus("processing")
      setError(null)
      trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
        productId: plan.id,
        price: plan.price,
        currency: plan.currency,
        metadata: { kind: "upsell" },
      })

      try {
        const result = await paymentService.createUpsellSubscription({
          planId: plan.priceId || plan.id,
          email,
          subscriptionId,
          extra,
        })
        setStatus("success")
        trackEvent(ANALYTICS_EVENTS.PURCHASE, {
          productId: plan.id,
          transactionId: result.id,
          price: plan.price,
          currency: plan.currency,
          metadata: { kind: "upsell" },
        })
        return result.id
      } catch (err) {
        const msg = paymentErrorMessage(err, "Could not add the offer.")
        setStatus("error")
        setError(msg)
        showErrorToast("Payment failed", msg)
        trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, {
          productId: plan.id,
          metadata: { kind: "upsell", error: msg },
        })
        return null
      }
    },
    [status, subscriptionId, email],
  )

  return { status, error, canPurchase: Boolean(subscriptionId), purchase }
}
